import React, { useEffect, useMemo, useState } from 'react';
import { useRouter } from 'next/router';
import {
  Container,
  Gutter,
  Row,
  Col,
  useIsScreenSize,
  Typography,
  RelatedArticles,
  BlogHero,
  Socials,
  SocialIconsNames,
} from '@ipf-ui/components-library';
import {
  ExitPreview,
  Layout,
  PortableTextWrapper,
  parsePostData,
  getBlogTranslationByKey,
  translationsKeys,
  renderContentList,
  useScroll,
  getBlogSlugs,
  getSingleBlogArticleBySlug,
  getAllSettings,
  buildSanityImageUrl,
} from '@web-creditea/utils';
import { createClient } from '../../../lib/apollo-client';
import { MARKET, REVALIDATION_TIME, SANITY_CDN_IMAGE_URL } from '../../../src/constants';
import type { GetStaticPaths, GetStaticPropsContext, InferGetStaticPropsType } from 'next';
import Link from 'next/link';
import { getLocales } from '../../../src/utils/helpers/locales';
import { renderSections } from '@web-creditea/utils';

import { TextRaw } from '@web-creditea/interfaces';
import { getDataset } from 'apps/web-creditea-ee/src/utils/helpers/getDataset';

const BlogPost = ({
  language,
  post,
  preview,
  sections,
  settings,
  dataset,
}: InferGetStaticPropsType<typeof getStaticProps>) => {
  const router = useRouter();
  const { isScreenXS, isScreenSM, isScreenMD, isScreenAboveSM } = useIsScreenSize();
  const showSocials = isScreenXS || isScreenSM || isScreenMD;
  const { socials, kubernetesNamespace } = settings;
  const { overWriteCtaButton, CTAButton } = post;
  const locales = getLocales(router.locale);

  const relatedArticles = useMemo(() => {
    return post?.relatedArticles?.map((article) => ({
      ...parsePostData(article, language, locales),
      onClickLinkAction: () => router.push(article.slug.current),
    }));
  }, [post, router, language, locales]);

  const relatedArticlesTitle = useMemo(
    () => getBlogTranslationByKey(translationsKeys.relatedArticles, language) || 'Related Articles',
    [language]
  );

  const shareTheArticle = useMemo(
    () =>
      getBlogTranslationByKey(translationsKeys.shareTheArticle, language) || 'Share the article',
    [language]
  );

  const { y: offsetY } = useScroll();
  const [scrollTop, setScrollTop] = useState(0);

  useEffect(() => {
    post.showContentList && setScrollTop(offsetY);
  }, [offsetY, post.showContentList]);

  const urlFor = buildSanityImageUrl({
    dataset,
    cdnUrl: SANITY_CDN_IMAGE_URL,
    projectId: process.env.SANITY_PROJECT_ID,
  });

  return (
    <Layout
      title={post.title}
      settings={settings}
      seo={post.seoGroup}
      ctaButton={overWriteCtaButton && CTAButton}
      articleInfo={{
        _createdAt: post._createdAt,
        publishedDate: post.publishedDate,
      }}
    >
      <>
        <Gutter size={isScreenAboveSM ? 140 : 100} />
        {preview ? <ExitPreview /> : null}
        <BlogHero
          {...parsePostData(post, language, locales)}
          NextLink={Link}
          showContentList={post.showContentList}
          contentTitle={post.contentTitle}
          anchors={sections && renderContentList(sections)}
          offset={scrollTop}
        />
        <Gutter size={60} />
        <Container>
          <Row center="xs">
            <Col xs={4} md={12} lg={8}>
              {post?.content && (
                <PortableTextWrapper
                  urlFor={urlFor}
                  portableText={post?.content?.textRaw as TextRaw}
                />
              )}
              <Gutter size={140} />
              {showSocials && socials && (
                <>
                  <Typography.Header6>{shareTheArticle}</Typography.Header6>
                  <Socials
                    socialList={socials.map((el) => {
                      return {
                        ...el,
                        icon: el.icon as SocialIconsNames,
                      };
                    })}
                  />
                  <Gutter size={60} />
                </>
              )}
            </Col>
          </Row>
        </Container>
        {sections && renderSections(sections, kubernetesNamespace, MARKET)}
        {relatedArticles?.length > 0 && (
          <RelatedArticles
            title={relatedArticlesTitle}
            articles={relatedArticles}
            language={language}
            nextLink={Link}
            variant="gray"
          />
        )}
      </>
    </Layout>
  );
};

export const getStaticPaths: GetStaticPaths = async ({ locales }) => {
  const client = createClient();
  const allBlog = await getBlogSlugs(client)(
    locales.map((locale) => locale.toUpperCase()),
    MARKET
  );

  const paths = allBlog.map((page) => {
    return {
      params: { slug: page.slug.current },
      locale: page.language.toLowerCase(),
    };
  });

  return {
    paths,
    fallback: 'blocking',
  };
};

export async function getStaticProps({ params, locale, preview }: GetStaticPropsContext) {
  const client = createClient();

  const language = locale.toUpperCase();

  const pageData = await getSingleBlogArticleBySlug(client)(language, MARKET, preview, params.slug);
  if (!pageData) {
    return {
      notFound: true,
    };
  }

  const settings = await getAllSettings(client, MARKET, language, preview);

  return {
    props: {
      post: pageData,
      sections: pageData.sections,
      language,
      preview: !!preview,
      settings,
      dataset: getDataset(),
    },
    revalidate: REVALIDATION_TIME,
  };
}

export default BlogPost;
